import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { ShoppingCart, Minus, Plus, Trash2, ExternalLink, Loader2, Gift } from "lucide-react";
import { useCartStore } from "@/stores/cartStore";
import { formatPrice } from "@/lib/shopify";

export const CartDrawer = ({ triggerClassName }: { triggerClassName?: string }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const { 
    items, 
    isLoading,
    isSyncing,
    updateQuantity, 
    removeItem, 
    toggleGiftWrap,
    refreshCheckoutUrl,
    syncCart,
    getTotalItems,
    getTotalPrice,
  } = useCartStore();
  
  const totalItems = getTotalItems();
  const totalPrice = getTotalPrice();

  useEffect(() => { if (isOpen) syncCart(); }, [isOpen, syncCart]);

  const handleCheckout = async () => {
    // Open window immediately on user gesture to avoid iOS Safari popup blocker
    const newWindow = window.open('about:blank', '_blank');
    const checkoutUrl = await refreshCheckoutUrl();
    if (checkoutUrl && newWindow) {
      newWindow.location.href = checkoutUrl;
      setIsOpen(false);
    } else if (newWindow && !checkoutUrl) {
      newWindow.close();
    } else if (checkoutUrl) {
      // Fallback: direct navigation if popup was still blocked
      window.location.href = checkoutUrl;
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className={triggerClassName || "relative text-cream hover:text-bronze"} aria-label="Open cart">
          <ShoppingCart className="h-5 w-5" />
          {totalItems > 0 && (
            <Badge className="absolute -top-2 -right-2 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs bg-bronze text-ivory">
              {totalItems}
            </Badge>
          )}
        </Button>
      </SheetTrigger>
      
      <SheetContent className="w-full sm:max-w-lg flex flex-col h-full bg-background">
        <SheetHeader className="flex-shrink-0">
          <SheetTitle className="font-display text-foreground">{t('cart.title')}</SheetTitle>
          <SheetDescription>
            {totalItems === 0 ? t('cart.empty') : t('cart.itemCount', { count: totalItems })}
          </SheetDescription>
        </SheetHeader>
        
        <div className="flex flex-col flex-1 pt-6 min-h-0">
          {items.length === 0 ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <ShoppingCart className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">{t('cart.empty')}</p>
              </div>
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto pr-2 min-h-0">
                <div className="space-y-4">
                  {items.map((item) => (
                    <div key={item.variantId} className="flex gap-4 p-3 bg-secondary/30 rounded-lg">
                      <div className="w-16 h-16 bg-secondary rounded-md overflow-hidden flex-shrink-0">
                        {item.product.node.images?.edges?.[0]?.node && (
                          <img
                            src={item.product.node.images.edges[0].node.url}
                            alt={item.product.node.title}
                            className="w-full h-full object-cover"
                          />
                        )}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <h4 className="font-heading font-medium text-foreground truncate">
                          {item.product.node.title}
                        </h4>
                        {item.variantTitle !== "Default Title" && (
                          <p className="text-sm text-muted-foreground">
                            {item.variantTitle}
                          </p>
                        )}
                        <p className="font-body font-semibold text-bronze">
                          {formatPrice(item.price.amount, item.price.currencyCode)}
                        </p>
                        <label className="flex items-center gap-1.5 mt-1 cursor-pointer">
                          <Checkbox
                            checked={!!item.giftWrap}
                            onCheckedChange={() => toggleGiftWrap(item.variantId)}
                            disabled={isLoading}
                            className="h-3.5 w-3.5"
                          />
                          <Gift className="h-3 w-3 text-bronze" />
                          <span className="text-xs text-muted-foreground">
                            {t('cart.giftWrap')} {t('cart.giftWrapPrice')}
                          </span>
                        </label>
                      </div>
                      <div className="flex flex-col items-end gap-2 flex-shrink-0">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-10 w-10 min-h-[44px] min-w-[44px] text-muted-foreground hover:text-destructive"
                          onClick={() => removeItem(item.variantId)}
                          disabled={isLoading}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                        
                        <div className="flex items-center gap-1">
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-8 w-8 min-h-[44px] min-w-[44px]"
                            onClick={() => updateQuantity(item.variantId, item.quantity - 1)}
                            disabled={isLoading}
                          >
                            <Minus className="h-3.5 w-3.5" />
                          </Button>
                          <span className="w-8 text-center text-sm font-medium">{item.quantity}</span>
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-8 w-8 min-h-[44px] min-w-[44px]"
                            onClick={() => updateQuantity(item.variantId, item.quantity + 1)}
                            disabled={isLoading}
                          >
                            <Plus className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="flex-shrink-0 space-y-3 pt-4 border-t border-border mt-4">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-heading font-semibold text-foreground">{t('common.total')}</span>
                  <span className="text-xl font-bold text-bronze">
                    {formatPrice(totalPrice.toString(), items[0]?.price.currencyCode || 'EUR')}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">
                  {t('cart.shippingAtCheckout')}
                </p>
                
                <Button 
                  onClick={handleCheckout}
                  className="w-full bg-bronze hover:bg-bronze/90 text-ivory" 
                  size="lg"
                  disabled={items.length === 0 || isLoading || isSyncing}
                >
                  {isLoading || isSyncing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t('cart.creatingCheckout')}
                    </>
                  ) : (
                    <>
                      <ExternalLink className="w-4 h-4 mr-2" />
                      {t('common.checkout')}
                    </>
                  )}
                </Button>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
